'use client'

import { useEffect, useRef, useState } from 'react'
import {
  IconArrowLeft,
  IconArrowRight,
  IconCircleArrowRightFilled
} from '@tabler/icons-react'
import Link from 'next/link'

import { RecommendationMetadataType } from 'lib/synerise'
import { SeoListName } from 'shared/analytics'
import IconButton from 'shared/ui/IconButton'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import { ProductTypes } from '../types/products'

import ProductCard from './ProductCard'

import styles from './SwipeableSlider.module.scss'

const SCROLL_BY_ITEMS = 1
const MIN_SCROLL_THRESHOLD = 80

const calculateScrollbyWidth = (
  sliderRef: React.RefObject<HTMLUListElement | null>
) => {
  const listElement = sliderRef.current?.querySelector('li')

  if (listElement) {
    const computedStyle = window.getComputedStyle(listElement)

    const marginRight = parseInt(
      computedStyle.marginRight.replace('px', '') || '0'
    )

    const marginLeft = parseInt(
      computedStyle.marginLeft.replace('px', '') || '0'
    )

    const elWidth = listElement.clientWidth

    return elWidth + marginRight + marginLeft
  }

  return 254
}
const getPadding = (sliderRef: React.RefObject<HTMLUListElement | null>) => {
  const listElement = sliderRef.current?.querySelector('li')

  if (listElement) {
    const computedStyle = window.getComputedStyle(listElement)

    const paddingRight = parseInt(
      computedStyle.paddingRight.replace('px', '') || '0'
    )

    const paddingLeft = parseInt(
      computedStyle.paddingLeft.replace('px', '') || '0'
    )

    return paddingRight + paddingLeft
  }

  return 0
}

interface SwipeableSliderProps {
  id: string
  products: ProductTypes[]
  title: string
  seoButtonPlace: string
  listName: SeoListName
  withRedirectTile?: boolean
  redirectButton?: {
    label: string
    path: string
  }
  redirectTile?: {
    label: string
    path: string
  }
  syneriseRecommendationMetadata?: RecommendationMetadataType | null
}

const SwipeableSlider = ({
  id,
  products,
  title,
  seoButtonPlace,
  redirectButton,
  redirectTile,
  listName,
  syneriseRecommendationMetadata
}: SwipeableSliderProps) => {
  const sliderRef = useRef<HTMLUListElement | null>(null)

  const [isAtBeginning, setIsAtBeginning] = useState<boolean>(true)
  const [isAtEnd, setIsAtEnd] = useState<boolean>(false)
  const [scrollbyWidth, setScrollbyWidth] = useState<number>(254)

  useEffect(() => {
    const sliderCurrent = sliderRef.current
    const handleScroll = () => {
      setScrollbyWidth(calculateScrollbyWidth(sliderRef))
    }

    if (sliderCurrent) {
      setScrollbyWidth(calculateScrollbyWidth(sliderRef))
      sliderCurrent.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (sliderCurrent) {
        sliderCurrent.removeEventListener('scroll', handleScroll)
      }
    }
  }, [sliderRef.current])

  const handlePrevClick = () => {
    if (sliderRef.current && !isAtBeginning) {
      const { scrollLeft } = sliderRef.current

      if (scrollLeft <= 0) return

      const nextScrollLeftPosition = scrollLeft - scrollbyWidth

      const distanceToShowWholeProduct = nextScrollLeftPosition % scrollbyWidth

      let scrollBy =
        distanceToShowWholeProduct === 0
          ? scrollbyWidth * SCROLL_BY_ITEMS
          : scrollbyWidth * (SCROLL_BY_ITEMS - 1) + distanceToShowWholeProduct

      if (scrollBy < MIN_SCROLL_THRESHOLD) {
        scrollBy += scrollbyWidth
      }

      const paddingSlider = getPadding(sliderRef)

      const scrollByWithPadding = -Math.abs(scrollBy) + paddingSlider

      sliderRef.current.scrollBy({
        left:
          scrollLeft < scrollByWithPadding ? scrollLeft : scrollByWithPadding,
        behavior: 'smooth'
      })
    }
  }

  const handleNextClick = () => {
    if (sliderRef.current) {
      const { scrollLeft, clientWidth, scrollWidth } = sliderRef.current

      if (scrollLeft + clientWidth >= scrollWidth) return

      const nextScrollLeftPosition = scrollLeft + clientWidth + scrollbyWidth

      const distanceToShowWholeProduct = nextScrollLeftPosition % scrollbyWidth

      let scrollBy =
        distanceToShowWholeProduct === 0
          ? scrollbyWidth * SCROLL_BY_ITEMS
          : scrollbyWidth * (SCROLL_BY_ITEMS - 1) +
            (scrollbyWidth - distanceToShowWholeProduct)

      if (scrollBy < MIN_SCROLL_THRESHOLD) {
        scrollBy += scrollbyWidth
      }

      const scrollToMax = scrollWidth - clientWidth - scrollLeft - scrollBy

      if (scrollToMax < MIN_SCROLL_THRESHOLD) {
        scrollBy += scrollToMax
      }

      const paddingSlider = getPadding(sliderRef)

      sliderRef.current.scrollBy({
        left: scrollBy + paddingSlider,
        behavior: 'smooth'
      })
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      if (sliderRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current

        setIsAtBeginning(scrollLeft <= 0)
        setIsAtEnd(scrollLeft + clientWidth >= scrollWidth)
      }
    }

    const currentSliderRef = sliderRef.current

    if (currentSliderRef) {
      currentSliderRef.addEventListener('scroll', handleScroll)
      handleScroll()
    }

    return () => {
      if (currentSliderRef) {
        currentSliderRef.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  return isNotNilOrEmpty(products) ? (
    <div id={id} aria-label={title} role='region'>
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        <div className={styles.controls}>
          {redirectButton && (
            <Link
              className={styles.seeMore}
              href={redirectButton?.path}
              {...addTestId('slider-see-more-button')}
            >
              {redirectButton?.label}
            </Link>
          )}
          <div className={styles.controlButtons}>
            <IconButton
              variant='gray'
              onClick={handlePrevClick}
              size='medium'
              icon={<IconArrowLeft />}
              disabled={isAtBeginning}
              aria-label='Pokaz poprzedni'
            />
            <IconButton
              variant='gray'
              onClick={handleNextClick}
              size='medium'
              icon={<IconArrowRight />}
              aria-label='Pokaz następny'
              disabled={isAtEnd}
            />
          </div>
        </div>
      </div>
      <ul
        className={styles.sliderWrapper}
        ref={sliderRef}
        {...addTestId(`${id || title}-products-list-slider`)}
      >
        {products.map((product, idx) => (
          <li
            className={styles.productWrapper}
            key={product.id}
            {...addTestId(`${id}-product-card-slider`)}
          >
            <ProductCard
              product={product}
              productPath={`/${product.slug}`}
              seoButtonPlace={seoButtonPlace}
              idxOnList={idx}
              listName={listName}
              id={`${id}-${product.id}`}
              syneriseRecommendationMetadata={syneriseRecommendationMetadata}
            />
          </li>
        ))}
        {redirectTile && (
          <li className={styles.productWrapper}>
            <Link className={styles.redirectTile} href={redirectTile.path}>
              {redirectTile.label}
              <IconCircleArrowRightFilled size={60} />
            </Link>
          </li>
        )}
      </ul>
    </div>
  ) : null
}

export default SwipeableSlider
